import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CustomerGroup, Customer, Country, Factory, Line, Machine } from '../services/BackendDataService';
import { LineIcon } from './LineIcon';
import { MachineIcon } from './MachineIcon';
import { getCountryFlag } from '../utils/countryFlags';
import {
  translateCountryName, 
  translateMachineType
} from '../utils/sapMetadataTranslations';
import { useThemeClasses } from '../providers/ThemeProvider';
import { useSAPDataStore } from '../store/SAPDataStore';
import { cx } from '@digitalisation/enlixy-comp';

interface SAPTreeViewOptimizedProps {
  sapData: Record<string, CustomerGroup>;
  onMachineClick?: (machine: Machine, context: { line: Line; factory: Factory; country: Country; customer: Customer }) => void;
  onLineClick?: (line: Line, context: { factory: Factory; country: Country; customer: Customer }) => void;
  onFactoryClick?: (factory: Factory, context: { country: Country; customer: Customer }) => void;
  expandedNodes: Set<string>;
  onToggleNode: (nodeId: string) => void;
  isExpanded: (nodeId: string) => boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  maxVisibleNodes?: number; // Limit visible nodes for performance
  isMobile?: boolean;
}

// Flattened tree structure for efficient rendering
interface FlatTreeNode {
  id: string;
  label: string;
  icon: string | React.ReactNode;
  level: number;
  type: 'group' | 'region' | 'country' | 'customer' | 'factory' | 'line' | 'machine';
  count?: number;
  isExpandable: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  data?: any; // Original data for callbacks
  context?: any; // Context for callbacks
}

// Memoized tree node component
const TreeNodeMemo = React.memo<{
  node: FlatTreeNode;
  onToggle: (nodeId: string) => void;
}>(({ node, onToggle }) => {
  const theme = useThemeClasses();
  const indentLevel = node.level * 20;
  
  return (
    <div 
      className={cx(
        "flex items-center p-2 cursor-pointer transition-colors duration-200",
        theme.bgSecondary,
        theme.borderPrimary,
        node.onClick ? `${theme.textPrimary} ${theme.hover}` : theme.textSecondary
      )}
      style={{ paddingLeft: `${12 + indentLevel}px` }}
      onClick={(e) => {
        e.stopPropagation();
        if (node.onClick) {
          node.onClick();
        } else if (node.isExpandable) {
          onToggle(node.id);
        }
      }}
    >
      {/* Chevron for expandable nodes */}
      {node.isExpandable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.id);
          }}
          className={`mr-2 p-1 rounded transition-colors ${theme.hover} hover:${theme.hoverSecondary}`}
        >
          {node.isExpanded ? (
            <ChevronDownIcon className={`w-4 h-4 ${theme.textSecondary}`} />
          ) : (
            <ChevronRightIcon className={`w-4 h-4 ${theme.textSecondary}`} />
          )}
        </button>
      )}
      
      {/* Icon */}
      {node.icon && (
        <div className="mr-2 flex items-center justify-center w-12 h-12">
          {typeof node.icon === 'string' ? (
            <span className="text-lg">{node.icon}</span>
          ) : (
            node.icon
          )}
        </div>
      )}
      
      {/* Label */}
      <span className={cx(
        "flex-1 font-medium",
        theme.textPrimary,
        node.onClick && `hover:${theme.linkPrimary} cursor-pointer`
      )}>
        {node.label}
      </span>
      
      {/* Count badge */}
      {node.count !== undefined && (
        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${theme.bgTertiary} ${theme.textSecondary}`}>
          {node.count}
        </span>
      )}
    </div>
  );
});

TreeNodeMemo.displayName = 'TreeNodeMemo';

// Memoized machine node component
const MachineNodeMemo = React.memo<{
  machine: Machine;
  level: number;
  context: { line: Line; factory: Factory; country: Country; customer: Customer };
  onMachineClick?: (machine: Machine, context: any) => void;
}>(({ machine, level, context, onMachineClick }) => {
  const { t, i18n } = useTranslation();
  const theme = useThemeClasses();
  const showOnlyWithData = useSAPDataStore(state => state.showOnlyWithData);
  
  // Translate machine type
  const translatedBautyp = useMemo(() => 
    translateMachineType(machine.bautyp || machine.machineType || '', i18n.language),
    [machine.bautyp, machine.machineType, i18n.language]
  );
  
  return (
    <div 
      className={`flex items-center p-3 cursor-pointer transition-colors ${theme.bgSecondary} ${theme.hover} ${theme.borderPrimary} border-l rounded-r-lg`}
      style={{ paddingLeft: `${32 + level * 20}px` }}
      onClick={() => onMachineClick?.(machine, context)}
    >
      <div className="mr-3 flex-shrink-0">
        <MachineIcon 
          machineId={machine.maschinennummer || machine.machineId}
          machineType={machine.bautyp || machine.machineType}
          size={120}
          className={theme.textInfo}
        />
      </div>
      <span className="flex-1 flex flex-col">
        <span className={`font-medium ${theme.textPrimary}`}>
          {machine.maschinennummer} - {(machine.bautyp || machine.machineType || translatedBautyp || '').replace(new RegExp(`^${machine.maschinennummer}\\s*[-–]\\s*`), '')}
        </span>
      </span>
      {/* Show No Data badge only for machines without data when showing all machines */}
      {showOnlyWithData && !machine.datenVerfuegbar && (
        <span className={cx(
          "px-2 py-1 rounded-full text-xs font-medium",
          `${theme.bgError} ${theme.textWhite}`
        )}>
          {t('noData') || 'No Data'}
        </span>
      )}
    </div>
  );
});

MachineNodeMemo.displayName = 'MachineNodeMemo';

export const SAPTreeViewOptimized: React.FC<SAPTreeViewOptimizedProps> = ({ 
  sapData, 
  onMachineClick, 
  onLineClick, 
  onFactoryClick,
  expandedNodes,
  onToggleNode,
  isExpanded,
  onExpandAll,
  onCollapseAll,
  maxVisibleNodes = 500, // Default limit for performance
  isMobile: _isMobile = false
}) => {
  const { t, i18n } = useTranslation();
  const theme = useThemeClasses();

  // Memoized flattened tree structure
  const flattenedTree = useMemo(() => {
    const nodes: (FlatTreeNode | { type: 'machine'; machine: Machine; level: number; context: any })[] = [];
    let nodeCount = 0;
    
    // Early return if too many nodes would be generated
    const checkNodeLimit = () => nodeCount < maxVisibleNodes;
    
    Object.entries(sapData).forEach(([groupKey, customerGroup]) => {
      if (!checkNodeLimit()) return;
      
      const groupId = `group-${groupKey}`;
      const groupExpanded = isExpanded(groupId);
      
      nodes.push({
        id: groupId,
        label: customerGroup.kundengruppe,
        icon: '🏢',
        level: 0,
        type: 'group',
        count: customerGroup.regionen?.length || 0,
        isExpandable: true,
        isExpanded: groupExpanded
      });
      nodeCount++;
      
      if (groupExpanded && customerGroup.regionen) {
        customerGroup.regionen.forEach((region) => {
          if (!checkNodeLimit()) return;
          
          const regionId = `region-${groupKey}-${region.region}`;
          const regionExpanded = isExpanded(regionId);
          
          nodes.push({
            id: regionId,
            label: region.region,
            icon: '🌍',
            level: 1,
            type: 'region',
            count: region.laender?.length || 0,
            isExpandable: true,
            isExpanded: regionExpanded
          });
          nodeCount++;
          
          if (regionExpanded && region.laender) {
            region.laender.forEach((country) => {
              if (!checkNodeLimit()) return;
              
              const countryId = `country-${groupKey}-${region.region}-${country.land}`;
              const countryExpanded = isExpanded(countryId);
              
              nodes.push({
                id: countryId,
                label: translateCountryName(country.land, i18n.language),
                icon: getCountryFlag(country.land),
                level: 2,
                type: 'country',
                count: country.kunden?.length || 0,
                isExpandable: true,
                isExpanded: countryExpanded
              });
              nodeCount++;
              
              if (countryExpanded && country.kunden) {
                country.kunden.forEach((customer) => {
                  if (!checkNodeLimit()) return;
                  
                  const customerId = `customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`;
                  const customerExpanded = isExpanded(customerId);
                  
                  nodes.push({
                    id: customerId,
                    label: customer.kundenName,
                    icon: '👥',
                    level: 3,
                    type: 'customer',
                    count: customer.werke?.length || 0,
                    isExpandable: true,
                    isExpanded: customerExpanded
                  });
                  nodeCount++;
                  
                  if (customerExpanded && customer.werke) {
                    customer.werke.forEach((factory) => {
                      if (!checkNodeLimit()) return;
                      
                      const factoryId = `factory-${customer.kundennummer}-${factory.werkName}`;
                      const factoryExpanded = isExpanded(factoryId);
                      
                      nodes.push({
                        id: factoryId,
                        label: factory.werkName,
                        icon: '🏭',
                        level: 4,
                        type: 'factory',
                        count: factory.linien?.length || 0,
                        isExpandable: true,
                        isExpanded: factoryExpanded,
                        onClick: () => {
                          // First expand/collapse the factory node
                          onToggleNode(factoryId);
                          // Then call the factory click handler
                          onFactoryClick?.(factory, { country, customer });
                        },
                        data: factory,
                        context: { country, customer }
                      });
                      nodeCount++;
                      
                      if (factoryExpanded && factory.linien) {
                        factory.linien.forEach((line) => {
                          if (!checkNodeLimit()) return;
                          
                          const lineId = `line-${factory.werkName}-${line.linieBK}`;
                          const lineExpanded = isExpanded(lineId);
                          
                          // OEM number is on the line (not on machines)
                          const oemSuffix = line.oemNummer ? ` (OEM: ${line.oemNummer})` : '';
                          
                          nodes.push({
                            id: lineId,
                            label: (line.linieNummer && line.linieNummer !== 'undefined' ? `${line.linieNummer} - ${line.linieName.replace(new RegExp(`^${line.linieNummer}\\s*[-–]?\\s*`), '')}` : line.linieName) + oemSuffix,
                            icon: <LineIcon machineCount={line.totalMachineCount || line.maschinen?.length || 0} size={96} className={theme.textPrimary} />,
                            level: 5,
                            type: 'line',
                            count: line.maschinen?.length || 0,
                            isExpandable: true,
                            isExpanded: lineExpanded,
                            onClick: () => {
                              // For lines, clicking should navigate (not expand)
                              onLineClick?.(line, { factory, country, customer });
                            },
                            data: line,
                            context: { factory, country, customer }
                          });
                          nodeCount++;
                          
                          if (lineExpanded && line.maschinen) {
                            line.maschinen.forEach((machine) => {
                              if (!checkNodeLimit()) return;
                              
                              nodes.push({
                                type: 'machine',
                                machine,
                                level: 6,
                                context: { line, factory, country, customer }
                              });
                              nodeCount++;
                            });
                          }
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
    
    return { nodes, totalNodes: nodeCount, truncated: nodeCount >= maxVisibleNodes };
  }, [sapData, expandedNodes, isExpanded, maxVisibleNodes, i18n.language, theme.textPrimary, onFactoryClick, onLineClick]);

  // Optimized expand/collapse handlers
  const handleExpandAll = useCallback(() => {
    if (onExpandAll) {
      onExpandAll();
    }
  }, [onExpandAll]);
  
  const handleCollapseAll = useCallback(() => {
    if (onCollapseAll) {
      onCollapseAll();
    }
  }, [onCollapseAll]);

  if (Object.keys(sapData).length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center ${theme.bgSecondary} rounded-lg`}>
        <div className="text-6xl mb-4">🌳</div>
        <h3 className={`text-xl font-semibold mb-2 ${theme.textPrimary}`}>{t('noSAPData') || 'No SAP Data Available'}</h3>
        <p className={theme.textSecondary}>{t('loadSAPData') || 'Please load SAP data to see the tree structure.'}</p>
      </div>
    );
  }

  return (
    <div className={`${theme.bgPrimary} rounded-lg overflow-hidden flex flex-col h-full`}>
      <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} ${theme.borderPrimary} border-b flex-shrink-0`}>
        <div className={`text-lg font-semibold ${theme.textPrimary}`}>
          {t('List of available assets') || 'SAP Structure'}
        </div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgInfo} ${theme.textWhite} hover:opacity-90`}
            onClick={handleExpandAll}
            type="button"
          >
            {t('expandAll') || 'Expand All'}
          </button>
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgSecondary} ${theme.textSecondary} ${theme.borderPrimary} border hover:${theme.hoverSecondary}`}
            onClick={handleCollapseAll}
            type="button"
          >
            {t('collapseAll') || 'Collapse All'}
          </button>
        </div>
      </div>
      
      {/* Performance info */}
      {flattenedTree.truncated && (
        <div className={`p-2 text-sm ${theme.bgWarning} ${theme.textPrimary} border-b flex-shrink-0`}>
          ⚡ Performance mode: Showing {flattenedTree.totalNodes} of {maxVisibleNodes} max nodes. 
          Collapse some sections to see more.
        </div>
      )}
      
      {/* Tree content */}
      <div className="flex-1 overflow-y-auto">
        {flattenedTree.nodes.map((node, index) => {
          if ('type' in node && node.type === 'machine' && 'machine' in node) {
            const machineKey = node.machine.maschineBK || node.machine.maschinennummer || node.machine.machineId || `machine-${index}`;
            const uniqueKey = `machine-${machineKey}-${index}`;
            
            return (
              <MachineNodeMemo
                key={uniqueKey}
                machine={node.machine}
                level={node.level}
                context={node.context}
                onMachineClick={onMachineClick}
              />
            );
          }
          
          return (
            <TreeNodeMemo
              key={node.id}
              node={node}
              onToggle={onToggleNode}
            />
          );
        })}
      </div>
      
      {/* Statistics */}
      <div className={`p-3 text-xs ${theme.bgSecondary} ${theme.textSecondary} border-t ${theme.borderPrimary} flex-shrink-0`}>
        Showing {flattenedTree.nodes.length} nodes 
        {flattenedTree.truncated && ` (limited to ${maxVisibleNodes} for performance)`}
      </div>
    </div>
  );
};
